/**
 * Which cash or bank account a payment went through.
 *
 * Offers only accounts that suit the method — cash into a cash account,
 * anything else into a bank or wallet — and "the default for this method",
 * which is where a payment naming no account lands. Credit moves no money, so
 * it offers nothing.
 */

import type { ReactNode } from 'react';
import { MONEY_ACCOUNT_KIND_LABELS } from '@cenox/shared';
import { Select } from '../../components/ui';
import { useMoneyAccounts } from './data';

export function AccountSelect({
  method,
  value,
  onChange,
  error,
}: {
  method: string;
  value: string;
  onChange: (accountId: string) => void;
  error?: string | undefined;
}): ReactNode {
  const accounts = useMoneyAccounts();
  if (method === 'credit' || !Array.isArray(accounts.data?.accounts)) return null;

  const suitable = accounts.data.accounts.filter(
    (a) => !a.archived && (method === 'cash' ? a.kind === 'cash' : a.kind !== 'cash'),
  );
  if (suitable.length === 0) return null;

  return (
    <Select
      label={method === 'cash' ? 'Cash account' : 'Bank account'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={error}
      options={[
        { value: '', label: 'Default for this method' },
        ...suitable.map((a) => ({
          value: a.id,
          label: `${a.name}${a.branchName ? ` · ${a.branchName}` : ''} (${MONEY_ACCOUNT_KIND_LABELS[a.kind]})`,
        })),
      ]}
    />
  );
}
